import supabase from '../supabase/supabase'

/**
 * Staff Service
 * Handles all staff-related database operations
 */

/**
 * Get current user's tenant ID
 */
const getCurrentTenantId = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('No authenticated user')
  }

  const { data, error } = await supabase
    .from('tenant_members')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single()

  if (error) throw error
  
  return data.tenant_id
}

/**
 * Get all staff for current tenant
 */
export const getAllStaff = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('staff')
      .select('*')
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching staff:', error)
    throw error
  }
}

/**
 * Get staff by ID
 */
export const getStaffById = async (staffId) => {
  try {
    const { data, error } = await supabase
      .from('staff')
      .select('*')
      .eq('id', staffId)
      .is('deleted_at', null)
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error fetching staff by ID:', error)
    throw error
  }
}

/**
 * Create new staff member via Edge Function
 */
export const createStaff = async (staffData) => {
  console.log('🚀 createStaff called with:', staffData);
  try {
    console.log('📡 Calling Edge Function...');
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'staff',
        ...staffData,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    console.log('📥 Edge Function response - data:', data);
    console.log('📥 Edge Function response - error:', error);

    // If there's an error object, throw it
    if (error) {
      console.error('❌ Edge Function returned error:', error);
      throw new Error(error.message || 'Failed to create staff member');
    }

    // If we have data, return it
    if (data) {
      console.log('✅ Staff created successfully');
      return data;
    }

    // If neither data nor error, something is wrong
    throw new Error('No response from server');
  } catch (error) {
    console.error('❌ Error in createStaff:', error);
    // Ensure we always throw a proper Error object with a message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to create staff member');
  }
}

/**
 * Update staff member via Edge Function
 */
export const updateStaff = async (staffId, updates) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'staff/update',
        staff_id: staffId,
        ...updates,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to update staff member');

    return data;
  } catch (error) {
    console.error('Error updating staff:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to update staff member');
  }
}

/**
 * Soft delete staff member via Edge Function
 */
export const deleteStaff = async (staffId) => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout - please try again')), 30000);
    });
    
    const invokePromise = supabase.functions.invoke('api', {
      body: {
        path: 'staff/delete',
        staff_id: staffId,
      },
    });
    
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);

    if (error) throw new Error(error.message || 'Failed to delete staff member');

    return data;
  } catch (error) {
    console.error('Error deleting staff:', error)
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error) || 'Failed to delete staff member');
  }
}

/**
 * Get staff documents
 */
export const getStaffDocuments = async (staffId) => {
  try {
    const { data, error } = await supabase
      .from('staff_documents')
      .select('*')
      .eq('staff_id', staffId)
      .order('uploaded_at', { ascending: false })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching staff documents:', error)
    throw error
  }
}

/**
 * Upload staff document
 */
export const uploadStaffDocument = async (staffId, documentData) => {
  try {
    const tenantId = await getCurrentTenantId()
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('staff_documents')
      .insert([
        {
          tenant_id: tenantId,
          staff_id: staffId,
          document_type: documentData.document_type,
          file_name: documentData.file_name,
          file_path: documentData.file_path,
          uploaded_by: user.id
        }
      ])
      .select()
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error uploading staff document:', error)
    throw error
  }
}

/**
 * Get staff timeline events from view
 */
export const getStaffTimeline = async (staffId) => {
  try {
    const { data, error } = await supabase
      .from('staff_timeline_view')
      .select('*')
      .eq('staff_id', staffId)
      .order('event_date', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching staff timeline:', error);
    throw error;
  }
};

/**
 * Get detailed performance metrics for a staff member
 */
export const getStaffPerformanceMetrics = async (staffId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    // Get staff member
    const { data: staff, error: staffError } = await supabase
      .from('staff')
      .select('*')
      .eq('id', staffId)
      .single();

    if (staffError) throw staffError;

    // Get case metrics
    const { data: cases, error: casesError } = await supabase
      .from('cases')
      .select('id, status, priority, created_at')
      .eq('staff_id', staffId)
      .is('deleted_at', null);

    if (casesError) throw casesError;

    const totalCases = cases.length;
    const completedCases = cases.filter(c => c.status === 'completed').length;
    const activeCases = cases.filter(c => c.status === 'active').length;
    const highPriorityCases = cases.filter(c => c.priority === 'high' && c.status === 'active').length;
    
    // Calculate overdue cases (active for more than 60 days)
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    const overdueCases = cases.filter(c => 
      c.status === 'active' && new Date(c.created_at) < sixtyDaysAgo
    ).length;

    // Get action metrics
    const { data: actions, error: actionsError } = await supabase
      .from('case_actions')
      .select('id, is_completed, is_legal_critical, is_acas_required')
      .in('stage_id', 
        await supabase
          .from('case_stages')
          .select('id')
          .in('case_id', cases.map(c => c.id))
          .is('deleted_at', null)
          .then(({ data }) => data?.map(s => s.id) || [])
      )
      .is('deleted_at', null);

    if (actionsError) throw actionsError;

    const totalActions = actions?.length || 0;
    const completedActions = actions?.filter(a => a.is_completed).length || 0;
    const pendingLegalActions = actions?.filter(a => 
      !a.is_completed && (a.is_legal_critical || a.is_acas_required)
    ).length || 0;

    // Calculate rates
    const completionRate = totalCases > 0 ? (completedCases / totalCases) * 100 : 0;
    const actionCompletionRate = totalActions > 0 ? (completedActions / totalActions) * 100 : 0;

    return {
      performanceRating: staff.performance_rating || 3.0,
      riskLevel: staff.risk_level || 'low',
      metrics: {
        totalCases,
        completedCases,
        activeCases,
        highPriorityCases,
        overdueCases,
        totalActions,
        completedActions,
        pendingLegalActions,
        completionRate: Math.round(completionRate),
        actionCompletionRate: Math.round(actionCompletionRate)
      }
    };
  } catch (error) {
    console.error('Error fetching staff performance metrics:', error);
    throw error;
  }
};

/**
 * Manually trigger staff metrics recalculation
 */
export const recalculateStaffMetrics = async (staffId) => {
  try {
    const { data, error } = await supabase.rpc('update_staff_metrics', {
      p_staff_id: staffId
    });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error recalculating staff metrics:', error);
    throw error;
  }
};

export default {
  getAllStaff,
  getStaffById,
  createStaff,
  updateStaff,
  deleteStaff,
  getStaffDocuments,
  uploadStaffDocument,
  getStaffTimeline,
  getStaffPerformanceMetrics,
  recalculateStaffMetrics
}